import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { loadCheckoutWebComponents, CardScheme } from '@checkout.com/checkout-web-components';

import {
    primary,
    inactive,
    error as errorColor,
    white,
    grey,
    black,
    success
} from '../../../../../scss/_variables.scss';
import { PATH } from '../../../../../constants/path';
import { CHECKOUT_API_KEY } from '../../../../../constants';

import { useCheckoutFlow } from './CheckoutFlowContext';

import { createOrder } from '../../../../../services/ebon/utils';
import { payWithCheckoutFlow } from '../../../../../services/checkout/utils';

import { LoadingComponent } from '../../../../loading/LoadingComponent';

import './CheckoutStyle.scss';

const CheckoutFlowForm = ({ userInfo, partnerId, i18n, token, ebon }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [FlowPaymentSession, setFlowPaymentSession] = useState(null);
    const { setIsValid, flowComponentRef, resetFlow } = useCheckoutFlow();

    const hasFreeFacial = ebon.ebons.freeFacialValue !== undefined;

    const processFlowPayment = async order => {
        return payWithCheckoutFlow(token, userInfo, partnerId, order.id);
    };

    const appearance = {
        colorAction: primary,
        colorBorder: 'transparent',
        colorDisabled: inactive,
        colorError: errorColor,
        colorFormBackground: white,
        colorFormBorder: grey,
        colorPrimary: black,
        colorSuccess: success,
        footnote: {
            fontFamily: '"Open Sans", sans-serif',
            fontSize: '12px',
            fontWeight: 400,
            letterSpacing: 0,
            lineHeight: 'normal'
        },
        label: {
            fontFamily: '"Open Sans", sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            letterSpacing: 0,
            lineHeight: 'normal'
        },
        input: {
            fontSize: '14px'
        },
        borderRadius: ['0px', '0px'],
        focusOutlineWidth: '0px'
    };

    const componentOptions = {
        card: {
            data: {
                cardholderName: `${userInfo.firstname} ${userInfo.lastname}`
            },
            displayCardholderName: 'bottom'
        },
        flow: {
            showPayButton: false,
            acceptedCardSchemes: [
                CardScheme.CartesBancaires,
                CardScheme.Visa,
                CardScheme.Mastercard
            ]
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const order = await createOrder(
                    token,
                    partnerId,
                    userInfo.phoneNumber,
                    !hasFreeFacial
                );

                let paymentSession = FlowPaymentSession;
                if (!paymentSession) {
                    paymentSession = await processFlowPayment(order);
                    setFlowPaymentSession(paymentSession);
                }

                const environment = CHECKOUT_API_KEY.includes('_sbox_') ? 'sandbox' : 'production';
                const orderId = order.id;
                const checkout = await loadCheckoutWebComponents({
                    publicKey: CHECKOUT_API_KEY,
                    environment,
                    locale: 'fr',
                    paymentSession,
                    appearance,
                    componentOptions,
                    onPaymentCompleted: (_component, paymentResponse) => {
                        console.log('Payment completed : ', paymentResponse);
                        const successUrl = `${PATH.EBON_CONFIRMATION_PATH}?orderId=${order.id}&cko-payment-id=${paymentResponse.id}`;
                        window.location.href = successUrl;
                    },
                    onChange: component => {
                        setIsValid(component.isValid());
                    },
                    onError: (component, error) => {
                        console.log('onError', error, 'Component', component);
                        const errorUrl = `${PATH.EBON_ERROR_PATH}-${partnerId}?paiement=error&orderId=${orderId}`;
                        window.location.href = errorUrl;
                    }
                });
                const flowComponent = checkout.create('flow');
                flowComponentRef.current = flowComponent;
                flowComponent.mount('#flow-container');
            } catch (error) {
                console.error('error while loading Checkout Flow : ', error);
                setError(true);
            } finally {
                setLoading(false);
            }
        })();

        // Réinitialise l'état partagé quand le formulaire est démonté
        // (ex. l'utilisateur change de moyen de paiement).
        return () => {
            resetFlow();
        };
    }, []);

    return (
        <div className="checkout-wrapper">
            {loading && (
                <div className="loading-wrapper">
                    <LoadingComponent />
                </div>
            )}

            <div id="flow-container" />

            {error && <p className="error-message">{i18n.ERROR_SESSION}</p>}
        </div>
    );
};

CheckoutFlowForm.propTypes = {
    userInfo: PropTypes.object,
    partnerId: PropTypes.number
};

const mapStateToProps = state => ({
    i18n: state.i18n.CHECKOUT.FLOW,
    token: state.authentication.token,
    ebon: state.ebon
});

export default connect(mapStateToProps)(CheckoutFlowForm);



// WEBPACK FOOTER //
// ./src/components/funnel/payment-step/payment-methods/checkout-flow/CheckoutFlowForm.js